import { TEST_CDK_URL, TEST_HUB_URL } from '@/constants/constants';
import { Company } from '@/context/types';
import { BaseFilter, PaginationResponse } from '@/hooks/usePaginationFilter';
import { CompanyQuery } from '@/store/slices/companySlice';
import { User } from '@/types';
import { apiFetch } from '@/utils/apiWrapper';
import {
  formatUrlWithFilters,
  formatUrlWithQueryParams,
} from '@/utils/helpers/formatUrlWithFilters';
import i18n from '@/utils/i18n';
import toast from 'react-hot-toast';

const companiesUrl = `${TEST_CDK_URL}company/`;
const companyManagementUrl = `${TEST_CDK_URL}company-management/`;
const companyPlansUrl = `${TEST_CDK_URL}company-plans/`;
export const saveCompanyLogo = async (
  accountId: string,
  companyId: string,
  base64Image: string,
) => {
  try {
    const response = await fetch(base64Image);
    const blob = await response.blob();
    const file = new File([blob], 'companyLogo.png', { type: blob.type });
    const formData = new FormData();
    formData.append('files', file);
    formData.append('accountId', accountId);
    formData.append('companyId', companyId);
    const uploadResponse = await fetch(`${TEST_HUB_URL}attachments`, {
      method: 'POST',
      body: formData,
    });
    if (!uploadResponse.ok) {
      throw new Error(`HTTP error! status: ${uploadResponse.status}`);
    }
    const data = await uploadResponse.json();
    return data[0];
  } catch (err) {
    toast.error(i18n.t('Errors.general'));
    throw err;
  }
};

export async function updateCompany(
  id: string,
  body: Partial<Company>,
): Promise<Company | undefined> {
  try {
    const res = await apiFetch(`${companiesUrl}${id}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    return res.json();
  } catch (err) {
    console.error('Error fetching company', err);
    throw err;
  }
}

export const getAllCompanies = async (
  query: CompanyQuery,
): Promise<PaginationResponse<Company>> => {
  try {
    let url = `${companiesUrl}companies/${query.id}`;
    if (query.filter) {
      url = formatUrlWithQueryParams(url, query.filter);
    }
    const res = await apiFetch(url);
    if (!res) {
      throw new Error('Error fetching companies');
    }
    const data = await res.json();
    return data as PaginationResponse<Company>;
  } catch (err) {
    console.error('Error fetching companies', err);
    throw new Error('Error fetching companies');
  }
};

export type CompanyParamsQuery = {
  organizationId: string;
  filters?: UserFilters;
};

export type UserFilters = Partial<Company> & BaseFilter;

export const getCompanies = async (
  query: CompanyParamsQuery,
): Promise<PaginationResponse<Company>> => {
  const { organizationId, filters } = query;
  try {
    let url = `${companiesUrl}${organizationId}`;
    if (filters) {
      url = formatUrlWithFilters(url, filters);
    }
    const res = await apiFetch(url);
    if (!res) {
      throw new Error('Error fetching users');
    }
    const data = await res.json();
    return data as PaginationResponse<Company>;
  } catch (err) {
    console.error('Error fetching company', err);
    throw new Error('Error fetching company');
  }
};

export type CompanyDashboardData = {
  products: number;
  orders: number;
  customers: number;
  numberOfOutlets: number;
  lastTransactionDate: string | null;
  totalPaymentVolume: number;
  totalTransaction: number;
};
export const getCompanyDataForDashboard = async (
  companyId: string,
  organizationId: string,
): Promise<CompanyDashboardData> => {
  try {
    const res = await apiFetch(
      `${companiesUrl}company-dashboard-data/${companyId}/${organizationId}`,
    );
    if (!res?.ok) {
      throw new Error(`Could not find organizations`);
    }
    const data = await res.json();
    return data || {};
  } catch (err) {
    console.error('Error fetching organizations', err);
    return {
      products: 0,
      orders: 0,
      customers: 0,
      numberOfOutlets: 0,
      lastTransactionDate: null,
      totalPaymentVolume: 0,
      totalTransaction: 0,
    };
  }
};
export type UpdateCompanyDto = {
  id: string;
  ownerId: string;
  body: {
    company: Partial<Company>;
    owner: Partial<User>;
  };
  planId?: string;
  organizationId: string;
};

export type UpdateCompanyAndOwnerResponse = {
  company: Partial<Company>;
  owner: Partial<Company>;
};
export const updateCompanyAndOwner = async (
  updateCompanyDto: UpdateCompanyDto,
): Promise<UpdateCompanyAndOwnerResponse> => {
  try {
    const res = await apiFetch(
      `${companyManagementUrl}update-company-and-owner/${updateCompanyDto.organizationId}/${updateCompanyDto.id}`,
      {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updateCompanyDto),
      },
    );
    if (!res) {
      throw new Error(`Could not update company`);
    }
    const data = await res.json();
    return data;
  } catch (err) {
    console.error('Error updating company', err);
    throw new Error('Error updating company');
  }
};

export type OnboardCompanyBody = {
  owner: Partial<User>;
  company: Partial<Company>;
};
export const onboardCompany = async (
  body: OnboardCompanyBody,
  organizationId: string,
): Promise<Company> => {
  try {
    const res = await apiFetch(`${companyManagementUrl}onboard-company/${organizationId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });

    if (!res) {
      throw new Error(`Could not onboard organization`);
    }

    const data = await res.json();
    return data;
  } catch (err) {
    console.error('Error onboarding organization', err);
    throw new Error('Error onboarding organization');
  }
};

export const getCompaniesCountByPlanId = async (organizationId: string, planId: string) => {
  const res = await apiFetch(`${companyPlansUrl}count/${organizationId}/${planId}`);
  if (!res) {
    throw new Error(`Could not get companies count by plan id`);
  }
  const data = await res.json();
  return data;
};

/**
 * Retrieves a company with its organization properties appended to it by the companyId
 * @param companyId - The id of the company to retrieve
 * @returns The company with its organization properties appended to it
 */
export const getCompanyById = async (companyId: string, organizationId: string) => {
  const res = await apiFetch(
    `${companiesUrl}company-with-organization/${companyId}/${organizationId}`,
  );
  if (!res) {
    throw new Error(`Could not get company by id`);
  }
  const data = await res.json();
  return data;
};
